import React, { useEffect, useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, UserIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Modal } from '../ui/Modal';
import { Table } from '../ui/Table';
import { Badge } from '../ui/Badge';
import { User, UserRole } from '../../types';
import { getRoleName, generateId } from '../../utils/helpers';
export function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'recepcionista' as UserRole
  });
  const [saveMessage, setSaveMessage] = useState('');
  // Load users from localStorage
  useEffect(() => {
    const savedUsers = localStorage.getItem('savidna-users');
    if (savedUsers) {
      try {
        const parsed = JSON.parse(savedUsers);
        setUsers(parsed.map((u: User) => ({
          ...u,
          createdAt: new Date(u.createdAt)
        })));
      } catch (e) {
        console.error('Error loading users:', e);
      }
    } else {
      // Default admin user
      const defaultUsers: User[] = [{
        id: 'admin-1',
        name: 'Administrador Sistema',
        email: 'admin@savidna.com',
        password: 'admin123',
        role: 'administrador',
        language: 'es',
        timezone: 'America/Santo_Domingo',
        createdAt: new Date()
      }];
      setUsers(defaultUsers);
      localStorage.setItem('savidna-users', JSON.stringify(defaultUsers));
    }
  }, []);
  // Save users to localStorage whenever they change
  useEffect(() => {
    if (users.length > 0) {
      localStorage.setItem('savidna-users', JSON.stringify(users));
    }
  }, [users]);
  const roleOptions = [{
    value: 'administrador',
    label: 'Administrador'
  }, {
    value: 'doctor',
    label: 'Doctor'
  }, {
    value: 'recepcionista',
    label: 'Recepcionista'
  }, {
    value: 'callcenter',
    label: 'Call Center'
  }];
  const handleOpenModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'recepcionista'
      });
    }
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'recepcionista'
    });
  };
  const handleSaveUser = () => {
    if (!formData.name || !formData.email) {
      alert('Por favor completa todos los campos requeridos');
      return;
    }
    if (!editingUser && !formData.password) {
      alert('La contraseña es requerida para nuevos usuarios');
      return;
    }
    if (editingUser) {
      // Update existing user
      const updatedUsers = users.map(u => u.id === editingUser.id ? {
        ...u,
        name: formData.name,
        email: formData.email,
        role: formData.role,
        ...(formData.password && {
          password: formData.password
        })
      } : u);
      setUsers(updatedUsers);
      setSaveMessage('Usuario actualizado correctamente');
    } else {
      // Create new user
      const newUser: User = {
        id: generateId(),
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        language: 'es',
        timezone: 'America/Santo_Domingo',
        createdAt: new Date()
      };
      setUsers([...users, newUser]);
      setSaveMessage('Usuario creado correctamente');
    }
    handleCloseModal();
    setTimeout(() => setSaveMessage(''), 3000);
  };
  const handleDeleteUser = (userId: string) => {
    if (confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      const updatedUsers = users.filter(u => u.id !== userId);
      setUsers(updatedUsers);
      setSaveMessage('Usuario eliminado correctamente');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };
  const getRoleBadgeVariant = (role: UserRole) => {
    switch (role) {
      case 'administrador':
        return 'success';
      case 'doctor':
        return 'info';
      case 'recepcionista':
        return 'warning';
      case 'callcenter':
        return 'default';
      default:
        return 'default';
    }
  };
  const columns = [{
    key: 'name',
    header: 'Nombre',
    render: (user: User) => <div className="flex items-center gap-3" data-id="element-1694">
          <div className="w-10 h-10 rounded-full bg-sky-100 dark:bg-sky-900 flex items-center justify-center text-sky-600 dark:text-sky-400 font-semibold" data-id="element-1695">
            {user.name.charAt(0)}
          </div>
          <div data-id="element-1696">
            <div className="font-medium text-gray-900 dark:text-white" data-id="element-1697">
              {user.name}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1698">
              {user.email}
            </div>
          </div>
        </div>
  }, {
    key: 'role',
    header: 'Rol',
    render: (user: User) => <Badge variant={getRoleBadgeVariant(user.role)} data-id="element-1699">
          {getRoleName(user.role)}
        </Badge>
  }, {
    key: 'createdAt',
    header: 'Fecha de Creación',
    render: (user: User) => <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1700">
          {new Date(user.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}
        </span>
  }, {
    key: 'actions',
    header: 'Acciones',
    render: (user: User) => <div className="flex items-center gap-2" data-id="element-1701">
          <button onClick={() => handleOpenModal(user)} className="p-2 text-sky-600 hover:bg-sky-50 dark:text-sky-400 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Editar usuario" data-id="element-1702">
            <EditIcon className="w-4 h-4" data-id="element-1703" />
          </button>
          <button onClick={() => handleDeleteUser(user.id)} className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Eliminar usuario" data-id="element-1704">
            <TrashIcon className="w-4 h-4" data-id="element-1705" />
          </button>
        </div>
  }];
  return <div className="space-y-6" data-id="element-1706">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1707">
        <div data-id="element-1708">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-1709">
            <UserIcon className="w-6 h-6 text-sky-600 dark:text-sky-400" data-id="element-1710" />
            Gestión de Usuarios
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-1711">
            Administra los usuarios del sistema y asigna roles
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} data-id="element-1712">
          <PlusIcon className="w-4 h-4" data-id="element-1713" />
          Nuevo Usuario
        </Button>
      </div>

      {saveMessage && <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm" data-id="element-1714">
          {saveMessage}
        </div>}

      {/* Users Table */}
      <Card data-id="element-1715">
        <Table columns={columns} data={users} keyExtractor={user => user.id} emptyMessage="No hay usuarios registrados" data-id="element-1716" />
      </Card>

      {/* User Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'} data-id="element-1717">
        <div className="space-y-4" data-id="element-1718">
          <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} placeholder="Ej: Juan Pérez" data-id="element-1719" />

          <Input label="Correo Electrónico" type="email" value={formData.email} onChange={e => setFormData({
          ...formData,
          email: e.target.value
        })} placeholder="usuario@ejemplo.com" data-id="element-1720" />

          <Input label={editingUser ? 'Nueva Contraseña (opcional)' : 'Contraseña'} type="password" value={formData.password} onChange={e => setFormData({
          ...formData,
          password: e.target.value
        })} placeholder="••••••••" data-id="element-1721" />

          <Select label="Rol del Usuario" options={roleOptions} value={formData.role} onChange={value => setFormData({
          ...formData,
          role: value as UserRole
        })} data-id="element-1722" />

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3" data-id="element-1723">
            <p className="text-sm text-blue-700 dark:text-blue-300" data-id="element-1724">
              <strong data-id="element-1725">Nota:</strong> Los permisos se asignan automáticamente
              según el rol seleccionado.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4" data-id="element-1726">
            <Button variant="secondary" onClick={handleCloseModal} data-id="element-1727">
              Cancelar
            </Button>
            <Button onClick={handleSaveUser} data-id="element-1728">
              {editingUser ? 'Actualizar' : 'Crear'} Usuario
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}